import { useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getAvailableDayFromCalendar } from 'lib/offer/offerCalendarUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { getDurationPackageOptionsByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'

// TEMPORARY merchandising hack for Salesforce offer 006RF00000Kl4O3YAJ:
// 70% of guests book as solo travellers, so always suggest its solo villa when available.
// Remove this hook and its call site when the offer ends.
export const ELYSIA_OFFER_ID = '006RF00000Kl4O3YAJ'
export const ELYSIA_SOLO_ROOM_TYPE_ID = 'afba8860-78b0-11f0-bb27-198967cf5697'

const ELYSIA_SOLO_OCCUPANCY: App.Occupants = {
  adults: 1,
  children: 0,
  infants: 0,
  teenagers: 0,
  childrenAge: [],
}
const SOLO_RECOMMENDATION_ROOM: OfferPageRoom = {
  id: 'elysia-solo-recommendation',
  ...ELYSIA_SOLO_OCCUPANCY,
}
const SOLO_OCCUPANCY_KEY = generateOccupancyStringByRoom(SOLO_RECOMMENDATION_ROOM)
const EMPTY_PACKAGE_OPTIONS: Array<App.OfferPackageOptionView> = []
const EMPTY_CALENDARS: Record<string, App.Calendar> = {}
const EMPTY_LOADING_STATE: Record<string, boolean> = {}

interface Params {
  room?: OfferPageRoom
}

export interface ElysiaSoloSuggestedPackageResult {
  option: App.OfferPackageOptionView
  message: string
  occupancyOverride: App.Occupants
}

interface HookResult {
  result: ElysiaSoloSuggestedPackageResult | undefined
  isLoading: boolean
}

export function findAvailableElysiaSoloPackage(
  packageOptions: Array<App.OfferPackageOptionView>,
  checkInDate: string | undefined,
  calendarsByPackageKey: Record<string, App.Calendar> = {},
): App.OfferPackageOptionView | undefined {
  return packageOptions.find(option => {
    const calendar = calendarsByPackageKey[option.package.uniqueKey]
    const selectedDateAvailable = !checkInDate || (
      !!calendar && !!getAvailableDayFromCalendar(checkInDate, calendar)
    )

    return option.package.roomType?.id === ELYSIA_SOLO_ROOM_TYPE_ID &&
      option.capacityValid &&
      option.hotelAvailable &&
      selectedDateAvailable
  })
}

/**
 * One-off recommendation for the Elysia solo-traveller room. Its availability must be fetched
 * using solo occupancy because the room is intentionally hidden for the default two-adult search.
 */
function useElysiaSoloSuggestedPackageOption({ room }: Params = {}): HookResult {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const offerPageState = useContext(OfferPageStateContext)
  const { offer, duration, checkInDate, airport, rooms } = offerPageState
  const appliesToOffer = !room && rooms.length === 1 && offer?.id === ELYSIA_OFFER_ID

  const soloPackages = useMemo(() => {
    if (!appliesToOffer || !offer) return []

    return offer.packages.filter(pkg =>
      pkg.duration === duration && pkg.roomType?.id === ELYSIA_SOLO_ROOM_TYPE_ID,
    )
  }, [appliesToOffer, duration, offer])

  const soloOfferPageState = useMemo(() => ({
    ...offerPageState,
    rooms: [SOLO_RECOMMENDATION_ROOM],
  }), [offerPageState])

  const soloPackageOptions = useAppSelector(appState => {
    if (!appliesToOffer) return EMPTY_PACKAGE_OPTIONS
    return getDurationPackageOptionsByRoom(soloOfferPageState, appState)[SOLO_RECOMMENDATION_ROOM.id] ?? EMPTY_PACKAGE_OPTIONS
  })
  const soloCalendars = useAppSelector(appState => appState.calendar.calendarsByOccupancy[SOLO_OCCUPANCY_KEY] ?? EMPTY_CALENDARS)
  const soloCalendarLoading = useAppSelector(appState => appState.calendar.calendarsByOccupancyLoading[SOLO_OCCUPANCY_KEY] ?? EMPTY_LOADING_STATE)

  useEffect(() => {
    if (!appliesToOffer || !offer || !checkInDate || soloPackages.length === 0) return

    dispatch(fetchCalendarsByOccupancy(offer.id, {
      occupants: [SOLO_RECOMMENDATION_ROOM],
      flightOrigin: airport?.code,
      packages: soloPackages,
    }))
  }, [airport?.code, appliesToOffer, checkInDate, dispatch, offer, soloPackages])

  return useMemo(() => {
    if (!appliesToOffer || soloPackages.length === 0) {
      return { result: undefined, isLoading: false }
    }

    const requireCalendar = !!checkInDate
    const hasResolvedCalendars = !requireCalendar || soloPackages.every(pkg => {
      const loadingState = soloCalendarLoading[pkg.uniqueKey]
      return !!soloCalendars[pkg.uniqueKey] || (loadingState !== undefined && !loadingState)
    })

    if (!hasResolvedCalendars) {
      return { result: undefined, isLoading: true }
    }

    const option = findAvailableElysiaSoloPackage(soloPackageOptions, checkInDate, soloCalendars)
    if (!option) {
      return { result: undefined, isLoading: false }
    }

    return {
      result: {
        option,
        message: intl.formatMessage({
          id: 'aIx2iMODLbkevsJA99f4',
          defaultMessage: 'Suggested for solo travelers',
          description: 'Temporary suggested-package banner for the Elysia Wellness Retreat solo-traveller package',
        }),
        occupancyOverride: ELYSIA_SOLO_OCCUPANCY,
      },
      isLoading: false,
    }
  }, [appliesToOffer, checkInDate, intl, soloCalendarLoading, soloCalendars, soloPackageOptions, soloPackages])
}

export default useElysiaSoloSuggestedPackageOption
